import { action, computed, observable } from 'mobx';
import { Api } from '../../../api/Api';
import { TrainingInDto } from '../../../api/generated';

export declare interface IAddTrainingStore {
  date: string;
  getDate: () => Date;
  setDate: (date: string) => void;
  time: string;
  setTime: (time: string) => void;
  duration: number;
  setDuration: (duration: number) => void;
  type: TrainingInDto.TypeEnum;
  setType: (type: TrainingInDto.TypeEnum) => void;
  types: TrainingInDto.TypeEnum[];
  description: string;
  setDescription: (desc: string) => void;
  intensity: number;
  setIntensity: (intensity: number) => void;
  valid: boolean;
  submit: () => Promise<void>;
}

export class AddTrainingStore implements IAddTrainingStore {

  @observable private _date : Date;
  @observable private _dateStr: string;
  @observable private _time: string;
  @observable private _duration: number = 60;
  @observable private _type: TrainingInDto.TypeEnum;
  @observable private _description: string;
  @observable private _intensity: number = 5;
  @observable private _valid: boolean = false;

  private readonly _api: Api;

  constructor(api: Api) {
    this._api = api;
    this.reset();
  }

  @computed get date() {
    return this._dateStr;
  }

  public getDate() {
    return this._date;
  }

  @action public setDate(date: string) {
    const s = date.split('-');
    this._date.setFullYear(Number(s[0]));
    this._date.setMonth(Number(s[1]));
    this._date.setDate(Number(s[2]));
    this._dateStr = date;
    this.validate();
  }

  @computed get time() {
    return this._time;
  }

  @action public setTime(time: string) {
    const s = time.split(':');
    this._date.setHours(Number(s[0]));
    this._date.setMinutes(Number(s[1]));
    this._time = time;
    this.validate();
  }

  @computed get duration() {
    return this._duration;
  }

  @action public setDuration(duration: number) {
    this._duration = duration;
    this.validate();
  }

  @computed get type() {
    return this._type;
  }

  @action public setType(type: TrainingInDto.TypeEnum) {
    this._type = type;
    this.validate();
  }

  @computed get types(): TrainingInDto.TypeEnum[] {
    return [
      TrainingInDto.TypeEnum.GYM,
      TrainingInDto.TypeEnum.BIKE,
      TrainingInDto.TypeEnum.RUN,
      TrainingInDto.TypeEnum.WALK,
      TrainingInDto.TypeEnum.DANCE,
      TrainingInDto.TypeEnum.SWIM,
      TrainingInDto.TypeEnum.FOOTBALL,
      TrainingInDto.TypeEnum.BASKETBALL,
      TrainingInDto.TypeEnum.VOLLEYBALL,
      TrainingInDto.TypeEnum.HANDBALL,
      TrainingInDto.TypeEnum.HOCKEY
    ];
  }

  @action public reset() {
    const date = new Date();
    date.setSeconds(0);
    date.setMilliseconds(0);
    this._date = date;
    this._time = `${this._localeString(date.getHours())}:${this._localeString(date.getMinutes())}`;
    this._dateStr = this._date.toISOString().slice(0, 10);
    this._description = '';
    this._intensity = 5;
    this._duration = 60;
  }

  @computed get description() {
    return this._description;
  }

  @action public setDescription(description: string) {
    this._description = description;
    this.validate();
  }

  @computed get intensity() {
    return this._intensity;
  }

  @action public setIntensity(intensity: number) {
    this._intensity = intensity;
    this.validate();
  }

  @action public submit() {
    return this._api.addTraining({
      description: this.description,
      duration: `PT${this.duration}M`,
      intensity: this._intensityEnum(),
      type: this.type,
      zonedTimestamp: this._date
    }).then(() => {
      this.reset();
    });
  }

  @computed get valid() {
    return this._valid;
  }

  @action public validate() {
    let valid = true;
    if (!this._time) {
      valid = false;
    }
    if (!this._duration) {
      valid = false;
    }
    if (!this._dateStr) {
      valid = false;
    }
    if (!this._type) {
      valid = false;
    }
    if (!this._intensity) {
      valid = false;
    }
    this._valid = valid;
  }

  private _localeString(n: number) {
    return n.toLocaleString(undefined, {minimumIntegerDigits: 2});
  }

  private _intensityEnum() {
    let val: TrainingInDto.IntensityEnum;
    switch (this._intensity) {
      case 1: val = TrainingInDto.IntensityEnum._1; break;
      case 2: val = TrainingInDto.IntensityEnum._2; break;
      case 3: val = TrainingInDto.IntensityEnum._3; break;
      case 4: val = TrainingInDto.IntensityEnum._4; break;
      case 5: val = TrainingInDto.IntensityEnum._5; break;
      case 6: val = TrainingInDto.IntensityEnum._6; break;
      case 7: val = TrainingInDto.IntensityEnum._7; break;
      case 8: val = TrainingInDto.IntensityEnum._8; break;
      case 9: val = TrainingInDto.IntensityEnum._9; break;
      case 10: val = TrainingInDto.IntensityEnum._10; break;
      default: throw new Error('unsupported intensity level')
    }
    return val;
  }

}



// WEBPACK FOOTER //
// ./src/diary/training/add/AddTrainingStore.ts